/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useContext, useState, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import { ViewModelServiceContext, ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useTranslator } from '@jutro/locale';
import { convertToSentenceCase } from 'ml-portals-util-js';
import '@xengage/gw-platform-translations';
import _ from 'lodash';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';

import MLBankAccountDetailsComponent from '../MLBankAccountDetailsComponent/MLBankAccountDetailsComponent';
import MLReferralAgentComponent from '../MLReferralAgentComponent/MLReferralAgentComponent';
import messages from './MLRecurringPaymentComponent.messages';
import metadata from './MLRecurringPaymentComponent.metadata.json5';
import styles from './MLRecurringPaymentComponent.module.scss';
import { Link } from '@jutro/router';

// ML: MLPaymentDetailsDirectiveCtrl.js, ml-payment-details.html

function MLRecurringPaymentComponent(props) {
    const translator = useTranslator();

    const {
        id,
        onValidate,
        showValidationErrors,
        paymentDetails,
        paymentInstruments,
        onPaymentInstrumentChange,
        addRecurringPaymentInstrument,
        canShowCreditCard,
        isCreditDebitSelected,
        isBankAccountSelected,
        hasPaymentInstrumentsForMethod,
        saveNewBankAccountPaymentInstrument,
        setHttpResponse,
        paymentDetailsUrl,
        canRemovePaymentInstrument,
        editPaymentInstrument,
        deletePaymentInstrument,
        allowAlternatePayor,
        quoteNumber,
        referralAgents,
        saveReferralAgentProducer,
        commissionOptions,
        setHasReferralAgentSelected,
        setIsReferralAgentValid,
        isGcsSalesAgent,
        isSplUser,
        setErrorTimestamp,
        errorTimestamp,
        isCreditCardOveridden,
        isPolicyChange,
        usePaymentusAch,
        showRecurringPaymentInstrumentError
    } = props;

    const {
        MLAccountPaymentMethods
    } = appConfig;

    const isGPA = appConfig.portalCode === 'GPA';
    const isQnB = appConfig.portalCode === 'QB';

    const { refreshData } = useDataRefresh();
    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const viewModelService = useContext(ViewModelServiceContext);
    const newBankAccountDetails = () => {
        return viewModelService.create({ accountHolder: { value: {} } }, 'pc', 'metlife.edge.capabilities.policyjob.binding.dto.BankAccountDetailsDTOML');
    };
    const [bankAccountDetails] = useState(() => { return newBankAccountDetails(); });

    const getPaymentNickname = (instrument) => {
        return instrument.nickname ? instrument.nickname : instrument.accountType_ML;
    };

    const onPaymentDetailsChange = () => {
        refreshData();
    };

    const updateBankAccountDetails = () => {
        refreshData();
    };

    const RECURRING_INSTRUMENT_TYPE = 'recurring';
    const onLoadPaymentDetailsFrame = () => {
        setHttpResponse(RECURRING_INSTRUMENT_TYPE);
    };

    const getNoPaymentInstrumentText = (method) => {
        return method && method.value === 'creditcard' ? translator(messages.CreditCardNotListed) : translator(messages.BankAccountNotListed)
    };

    const generateDataForRadioButton = (row) => {
        return {
            name: translator(messages.InstrumentOptionName, {
                nickname: getPaymentNickname(row),
                last4Digits: row.last4Digits
            }),
            code: row.token
        };
    };

    const generateActionColumn = (row, index) => {
        return (
            <div className='text-align-left actionsColumn'>
                <span className="edit-link">
                    <Link id={`edit${index}`} onClick={() => editPaymentInstrument(`${row.token}`, `${paymentDetails.paymentMethod.value}`, true)}>{translator(messages.Edit)}</Link>
                </span>
                {canRemovePaymentInstrument(`${row.token}`) && (
                    <>
                        <span id={`linkLineSpacing${index}`}> | </span>
                        <span className="edit-link">
                            <Link id={`remove${index}`} onClick={() => deletePaymentInstrument(`${row.token}`, `${paymentDetails.paymentMethod.value}`)}>{translator(messages.Remove)}</Link>
                        </span>
                    </>
                )}
            </div>
        );
    };

    const resolvers = {
        resolveComponentMap: {
            mlbankaccountdetailscomponent: MLBankAccountDetailsComponent,
            mlReferralAgentComponent: MLReferralAgentComponent
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            addRecurringPaymentInstrument: addRecurringPaymentInstrument
        }
    };

    const sortedData = useMemo(() => {
        const filteredData = _.filter(paymentInstruments, (paymentInstrument) => {
            return paymentInstrument.paymentMethod === paymentDetails.paymentMethod.value
                && (paymentInstrument.portalAvailable !== false || paymentInstrument.paymentMethod === MLAccountPaymentMethods.CREDIT_CARD);
        });
        return _.sortBy(filteredData, (pmi) => pmi.last4Digits);
    }, [MLAccountPaymentMethods.CREDIT_CARD, paymentDetails.paymentMethod.value, paymentInstruments]);

     const ifRecurringJustReturn = (paymentDetails?.paymentusIframeRecurringSameAsDownPayment_Ext?.value ?? false) || isSplUser();

    const overrides = {
        accountListRow: {
            visible: (ifRecurringJustReturn && (paymentDetails.paymentMethod.value === MLAccountPaymentMethods.CREDIT_CARD || (usePaymentusAch && paymentDetails.paymentMethod.value === MLAccountPaymentMethods.BANK_ACCOUNT)) || hasPaymentInstrumentsForMethod(paymentDetails.paymentMethod.value)) && paymentDetails.isPayThroughMortgage_ML.value === false,
            className: isQnB ? "radioButtonQnB" : ""
        },
        instrumentTable: {
            data: sortedData,
            visible: hasPaymentInstrumentsForMethod(paymentDetails.paymentMethod.value)
        },
        splNoPaymentInstruments: {
            visible: isSplUser() && ((isCreditDebitSelected() && !hasPaymentInstrumentsForMethod('creditcard')) || (usePaymentusAch && isBankAccountSelected() && !hasPaymentInstrumentsForMethod('ach'))),
            content: getNoPaymentInstrumentText(paymentDetails.paymentMethod)
        },
        accountColumn: {
            option: generateDataForRadioButton,
            value: paymentDetails.selectedPaymentInstrument_ML.value,
            onValueChange: onPaymentInstrumentChange
        },
        actionColumn: {
            cell: generateActionColumn
        },
        addRecurringPaymentButton: {
            visible: canShowCreditCard() || !isCreditDebitSelected(),
            className: isQnB ? "secondaryButton widthSmall" : "",
            content: isQnB ? convertToSentenceCase(translator(messages.AddAccount)) : translator(messages.AddAccount)
        },
        bankAccountDetailsComponent: {
            onValidate: setComponentValidation,
            showValidationErrors: showValidationErrors,
            bankAccountDetails,
            instrumentType: 'recurring',
            onModelChange: updateBankAccountDetails,
            onSave: saveNewBankAccountPaymentInstrument,
            setErrorTimestamp,
            errorTimestamp,
            visible: !usePaymentusAch && isBankAccountSelected() && hasPaymentInstrumentsForMethod(paymentDetails.paymentMethod.value) === false
        },
        referralAgentComponent: {
            visible: isGPA && isGcsSalesAgent() && !isPolicyChange,
            quoteNumber,
            referralAgents,
            commissionOptions,
            saveReferralAgentProducer,
            setHasReferralAgentSelected,
            setIsReferralAgentValid
        },
        paymentusIframe: {
            id: 'PaymentDetailsFrame',
            src: paymentDetailsUrl,
            height: '650px',
            className: isQnB ? 'PaymentusPaymentDetailsFrameQB' : 'PaymentusPaymentDetailsFrame',
            onLoad: onLoadPaymentDetailsFrame,
            visible: !isCreditCardOveridden && (isCreditDebitSelected() || (usePaymentusAch && isBankAccountSelected())) && hasPaymentInstrumentsForMethod(paymentDetails.paymentMethod.value) === false && !isSplUser()
        },
        alternatePayorRow: {
            visible: allowAlternatePayor(),
            className: isQnB ? 'radioButtonQnB' : '',
        },
        paymentInstrumentAlert: {
            visible: showRecurringPaymentInstrumentError,
            message: messages.preferredPaymentMethodErrorMessage
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            model={paymentDetails}
            onValidationChange={setComponentValidation}
            showErrors={showValidationErrors}
            onModelChange={onPaymentDetailsChange}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

MLRecurringPaymentComponent.propTypes = {
    id: PropTypes.string.isRequired,
    onValidate: PropTypes.func.isRequired,
    showValidationErrors: PropTypes.bool.isRequired,
    paymentDetails: PropTypes.shape({
        selectedPaymentInstrument_ML: PropTypes.shape({
            value: PropTypes.string
        }),
        paymentMethod: PropTypes.shape({
            value: PropTypes.string
        }),
        isPayThroughMortgage_ML: PropTypes.shape({
            value: PropTypes.bool
        })
    }).isRequired,
    onPaymentInstrumentChange: PropTypes.func.isRequired,
    paymentInstruments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    addRecurringPaymentInstrument: PropTypes.func.isRequired,
    canShowCreditCard: PropTypes.func.isRequired,
    isCreditDebitSelected: PropTypes.func.isRequired,
    isBankAccountSelected: PropTypes.func.isRequired,
    hasPaymentInstrumentsForMethod: PropTypes.func.isRequired,
    saveNewBankAccountPaymentInstrument: PropTypes.func,
    setHttpResponse: PropTypes.func,
    paymentDetailsUrl: PropTypes.string,
    canRemovePaymentInstrument: PropTypes.func.isRequired,
    editPaymentInstrument: PropTypes.func.isRequired,
    deletePaymentInstrument: PropTypes.func.isRequired,
    allowAlternatePayor: PropTypes.func,
    isCreditCardOveridden: PropTypes.bool,
    isGcsSalesAgent: PropTypes.func.isRequired,
    isSplUser: PropTypes.func.isRequired,
    isPolicyChange: PropTypes.bool,
    usePaymentusAch: PropTypes.bool,
    showRecurringPaymentInstrumentError: PropTypes.bool
};

MLRecurringPaymentComponent.defaultProps = {
    saveNewBankAccountPaymentInstrument: undefined,
    setHttpResponse: undefined,
    paymentDetailsUrl: undefined,
    allowAlternatePayor: undefined,
    isCreditCardOveridden: false,
    isPolicyChange: false,
    usePaymentusAch: false,
    showRecurringPaymentInstrumentError: false
};

export default MLRecurringPaymentComponent;
